import { api } from "../api";
import { APP_VERSION, isNativeRuntime, nativePlatform, PUSH_BUILD } from "./platform";

/**
 * FCM push for staff devices (WP08). Compiled in only when the native project has Firebase config (PUSH_BUILD);
 * without it the app builds and runs, and Device settings explain that notifications are not available.
 */
export type PushState = "unavailable" | "denied" | "prompt" | "granted";
const TOKEN_KEY = "spos.pushToken";
let listenersAdded = false;
let currentToken: string | null = null;
let onTap: ((route: string) => void) | null = null;
let onForeground: ((title: string, body: string) => void) | null = null;
let localeNow: "en" | "zh" | "ms" = "en";

export const pushAvailable = () => PUSH_BUILD && isNativeRuntime();

export async function pushPermission(): Promise<PushState> {
  if (!pushAvailable()) return "unavailable";
  const { PushNotifications } = await import("@capacitor/push-notifications");
  const p = await PushNotifications.checkPermissions();
  return p.receive === "granted" ? "granted" : p.receive === "denied" ? "denied" : "prompt";
}

async function sendToken(token: string) {
  currentToken = token;
  const { Preferences } = await import("@capacitor/preferences");
  await Preferences.set({ key: TOKEN_KEY, value: token });
  await api("/api/push/tokens", { method: "POST", body: { token, platform: nativePlatform() === "ios" ? "ios" : "android", appVersion: APP_VERSION.slice(0, 40), locale: localeNow, deviceLabel: (nativePlatform() === "ios" ? "iPad/iPhone" : "Android") + " app" } }).catch(() => undefined);
}

/** Registers this device for the signed-in member (asks for permission only when ask=true). */
export async function enablePush(o: { ask: boolean; locale: "en" | "zh" | "ms"; onTap: (route: string) => void; onForeground: (title: string, body: string) => void }): Promise<PushState> {
  if (!pushAvailable()) return "unavailable";
  localeNow = o.locale; onTap = o.onTap; onForeground = o.onForeground;
  const { PushNotifications } = await import("@capacitor/push-notifications");
  let perm = await PushNotifications.checkPermissions();
  if (perm.receive !== "granted" && perm.receive !== "denied" && o.ask) perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") return perm.receive === "denied" ? "denied" : "prompt";
  if (nativePlatform() === "android") {
    await PushNotifications.createChannel({ id: "spos_default", name: "Senvia POS", description: "Bookings, approvals and refunds", importance: 4, visibility: 1, lights: true, lightColor: "#176E60", vibration: true }).catch(() => undefined);
  }
  if (!listenersAdded) {
    listenersAdded = true;
    await PushNotifications.addListener("registration", (t) => { void sendToken(t.value); });
    await PushNotifications.addListener("registrationError", () => undefined);
    await PushNotifications.addListener("pushNotificationActionPerformed", (a) => {
      const route = (a.notification.data as { route?: string } | undefined)?.route;
      if (route && route.startsWith("/") && !route.startsWith("//")) onTap?.(route);
    });
    await PushNotifications.addListener("pushNotificationReceived", (n) => onForeground?.(n.title ?? "", n.body ?? ""));
  }
  await PushNotifications.register();
  return "granted";
}

/** Locale changed: refresh the token row so new messages arrive in the right language. */
export async function updatePushLocale(locale: "en" | "zh" | "ms") {
  localeNow = locale;
  if (currentToken) await sendToken(currentToken);
}

/** Before sign-out / server change: this device must stop receiving the salon's notifications. */
export async function unregisterPush() {
  if (!pushAvailable()) return;
  const { Preferences } = await import("@capacitor/preferences");
  const token = currentToken ?? (await Preferences.get({ key: TOKEN_KEY })).value;
  if (token) await api("/api/push/tokens/unregister", { method: "POST", body: { token } }).catch(() => undefined);
  currentToken = null;
}
